import React from 'react';

import { ReactComponent as GlobeIcon } from 'ui/asset/GlobeIcon.svg';
import { ReactComponent as MagnifierIcon } from 'ui/asset/MagnifierIcon.svg';
import { ReactComponent as ServerIcon } from 'ui/asset/ServerIcon.svg';
import { StyleUtil } from 'ui/util';
import classNames from './EABDDetailCircle.module.scss';

export type DetailCircleProgress = 'bottomLeft' | 'top' | 'bottomRight';

export interface DetailCircleProps
  extends Readonly<{
    progress: DetailCircleProgress;
  }> {}

export const DetailCircle = React.memo<DetailCircleProps>((props) => {
  const { progress } = props;

  const BottomLeftContent = React.useMemo(() => {
    const itemClassNames: string[] = [classNames.item];

    if (progress === 'bottomLeft') {
      itemClassNames.push(classNames.visible);
    }

    return (
      <div className={StyleUtil.mergeClassNames(itemClassNames)}>
        <div className={classNames.iconWrapper}>
          <GlobeIcon className={classNames.icon} />
        </div>
        <p className={classNames.body}>
          Manage networks
          <br />
          serving more than
          <br />
          <span className={classNames.emphasized}>1 billion subscriptions</span>
        </p>
        <p className={classNames.body}>
          More than{' '}
          <span className={classNames.emphasized}>
            5.5 billion
            <br />
            alarms
          </span>{' '}
          handled
          <br />
          yearly
        </p>
      </div>
    );
  }, [progress]);

  const TopContent = React.useMemo(() => {
    const itemClassNames: string[] = [classNames.item];

    if (progress === 'top') {
      itemClassNames.push(classNames.visible);
    }

    return (
      <div className={StyleUtil.mergeClassNames(itemClassNames)}>
        <div className={classNames.iconWrapper}>
          <ServerIcon className={classNames.icon} />
        </div>
        <p className={classNames.body}>
          <span className={classNames.emphasized}>175+ Terabytes</span> of data
          <br />
          processed each day
        </p>
        <p className={classNames.body}>
          <span className={classNames.emphasized}>Multi-vendor</span> (50%) and
          <br />
          <span className={classNames.emphasized}>multi-technology</span>
          <br />
          (2G-5G) data
        </p>
      </div>
    );
  }, [progress]);

  const BottomRightContent = React.useMemo(() => {
    const itemClassNames: string[] = [classNames.item];

    if (progress === 'bottomRight') {
      itemClassNames.push(classNames.visible);
    }

    return (
      <div className={StyleUtil.mergeClassNames(itemClassNames)}>
        <div className={classNames.iconWrapper}>
          <MagnifierIcon className={classNames.icon} />
        </div>
        <p className={classNames.body}>
          Master <span className={classNames.emphasized}>AI research</span> and
          <br />
          data-science expertise
        </p>
        <p className={classNames.body}>
          <span className={classNames.emphasized}>140+</span> years of telecom
          <br />
          domain experience
        </p>
      </div>
    );
  }, [progress]);

  const ContentElement = React.useMemo(() => {
    return (
      <div className={classNames.content}>
        {BottomLeftContent}
        {TopContent}
        {BottomRightContent}
      </div>
    );
  }, [BottomLeftContent, TopContent, BottomRightContent]);

  const ProgressElement = React.useMemo(() => {
    const progressClassNames: string[] = [classNames.progressCircle, classNames[progress]];

    return <div className={StyleUtil.mergeClassNames(progressClassNames)} />;
  }, [progress]);

  return (
    <div className={classNames.container}>
      <div className={classNames.borderedCircle}></div>
      <div className={classNames.filledCircle}>{ContentElement}</div>
      {ProgressElement}
    </div>
  );
});
